/**
 * VehicleTransfersTab — Aba "Transferências" do modal de detalhes do veículo
 *
 * Lista todos os clientes/contatos que já foram proprietários do veículo,
 * lendo o ledger de posse (`vehicle_ownerships`) via RPC
 * `get_vehicle_ownership_history`.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-26 v1.1.0 - Padroniza a ação com o botão compacto da aba Tarefas.
 * RELEASE: 2026-08-19 v1.0.0 - Criação inicial
 */
import { ArrowLeftRight, History, User } from 'lucide-react';
import { format, formatDistanceStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';
import { useVehicleOwnershipHistory } from '@/hooks/useVehicleOwnership';

interface VehicleTransfersTabProps {
  vehicleId: string;
  /** Só consulta quando a aba está visível */
  isActive?: boolean;
  /** Habilita o botão "Transferir veículo" (permissão de edição) */
  canTransfer?: boolean;
  /** Abre o modal de transferência */
  onTransfer?: () => void;
}

/** Formata o período de posse (início → fim) */
const formatPeriod = (from: string, to: string | null) => {
  const start = format(new Date(from), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  if (!to) return `${start} → atual`;
  return `${start} → ${format(new Date(to), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`;
};

/** Duração do período de posse */
const formatDuration = (from: string, to: string | null) =>
  formatDistanceStrict(new Date(from), to ? new Date(to) : new Date(), { locale: ptBR });

export function VehicleTransfersTab({
  vehicleId,
  isActive = true,
  canTransfer = false,
  onTransfer,
}: VehicleTransfersTabProps) {
  const { entries, isLoading } = useVehicleOwnershipHistory(vehicleId, isActive);

  return (
    <div className="space-y-3">
      {/* Cabeçalho da aba */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Transferências de propriedade</h3>
          <p className="text-xs text-muted-foreground">
            Clientes e contatos que já foram proprietários deste veículo.
          </p>
        </div>
        {canTransfer && (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={onTransfer}>
            <ArrowLeftRight className="h-3.5 w-3.5" />
            Transferir veículo
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed py-12 text-center">
          <History className="h-7 w-7 text-muted-foreground/60" />
          <p className="text-sm text-muted-foreground">
            Nenhum histórico de propriedade registrado para este veículo.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const ownerName =
              entry.customer_name || entry.contact_name || 'Proprietário não identificado';

            return (
              <div
                key={entry.ownership_id}
                className={cn(
                  'flex items-start gap-3 rounded-md border bg-card px-3 py-2.5',
                  entry.is_current && 'border-primary/40 ring-1 ring-primary/20',
                )}
              >
                <SmartAvatar name={ownerName} className="h-8 w-8 shrink-0" />

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{ownerName}</span>
                    {entry.is_current ? (
                      <Badge className="h-5 bg-primary/10 text-[10px] text-primary hover:bg-primary/10">
                        Proprietário atual
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="h-5 text-[10px] text-muted-foreground">
                        Anterior
                      </Badge>
                    )}
                  </div>

                  {/* Contato vinculado (quando diferente do cliente) */}
                  {entry.customer_name && entry.contact_name && (
                    <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <User className="h-3 w-3" />
                      {entry.contact_name}
                      {entry.contact_phone ? ` · ${entry.contact_phone}` : ''}
                    </p>
                  )}

                  <p className="text-[11px] text-muted-foreground">
                    {formatPeriod(entry.valid_from, entry.valid_to)} ·{' '}
                    {formatDuration(entry.valid_from, entry.valid_to)}
                  </p>

                  {entry.transfer_reason && (
                    <p className="text-xs text-foreground/80">
                      <span className="text-muted-foreground">Motivo: </span>
                      {entry.transfer_reason}
                    </p>
                  )}

                  {entry.created_by_name && (
                    <p className="text-[11px] text-muted-foreground">
                      Registrado por {entry.created_by_name}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
